import React, { FunctionComponent } from 'react';
import {
  Autocomplete,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import {
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  getFormatedDurationByMinutes,
  isCourseTutorEligibleForClass,
} from '../../../../../../../../../helper';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../../../components/Inputs';
import { ISelectedOption } from '../../../../../../../../../constants';
import { ICustomUpcomingClassItem } from '.';

interface UpcomingClassItemProps {
  section: ICustomUpcomingClassItem;
  tutorOptions: ISelectedOption[];
  updateUpcomingListItemAttributes: (
    id: string,
    attributes: Partial<ICustomUpcomingClassItem>
  ) => void;
}

const containerStyles: SxProps = {
  backgroundColor: '#F8FAFC',
  borderRadius: '8px',
};

export const renderTemplateItemStatusContent = (
  section: ICustomUpcomingClassItem
): React.ReactElement | null => {
  if (section.isLoading) {
    return (
      <>
        <CircularProgress size={15} />
        &nbsp;
      </>
    );
  }

  if (section.errorMessage) {
    return (
      <>
        <ErrorIcon sx={{ width: '15px', height: '15px' }} color="error" />
        &nbsp;
      </>
    );
  }

  if (section.successMessage) {
    return (
      <>
        <CheckCircleIcon
          sx={{ width: '15px', height: '15px' }}
          color="success"
        />
        &nbsp;
      </>
    );
  }

  return null;
};

const UpcomingClassItem: FunctionComponent<UpcomingClassItemProps> = ({
  section,
  tutorOptions,
  updateUpcomingListItemAttributes,
}) => {
  const dateTime = moment(section.dateTime).format('YYYY-MM-DD hh:mm A');
  const formatedDuration = getFormatedDurationByMinutes({
    minutes: section.duration || 0,
  });

  const filteredTutorOptions = [...tutorOptions].filter((item) => {
    let isEligible = false;

    if (item.courseTutor && section.dateTime && section.duration) {
      isEligible = isCourseTutorEligibleForClass(
        item.courseTutor,
        section.dateTime,
        section.duration || 0
      );
    }

    return isEligible;
  });

  return (
    <Card elevation={0} sx={containerStyles}>
      <CardContent style={{ padding: '16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack gap="3px">
            <Collapse in={Boolean(section.errorMessage)}>
              <Typography fontSize="14px" fontWeight={500} color="error.main">
                {section.errorMessage}
              </Typography>
            </Collapse>
            <Collapse in={Boolean(section.successMessage)}>
              <Typography fontSize="14px" fontWeight={500} color="success.main">
                {section.successMessage}
              </Typography>
            </Collapse>
            {section.title ? (
              <Typography
                fontSize="14px"
                fontWeight={600}
                color="#1E293B"
                display="flex"
                alignItems="center"
              >
                {renderTemplateItemStatusContent(section)}
                {section.title || 'Lesson'}
              </Typography>
            ) : null}
            <Typography fontSize="16px" fontWeight={500} color="#647488">
              {dateTime} - {formatedDuration}
            </Typography>
          </Stack>
          <Stack gap="8px" direction="row" alignItems="center">
            <div style={{ minWidth: '200px', maxWidth: '200px' }}>
              <Autocomplete
                fullWidth
                size="small"
                disabled={Boolean(section.successMessage) || section.isLoading}
                options={filteredTutorOptions}
                value={section.selectedTutorOption}
                popupIcon={TPAutoCompleteArrowIconComponent}
                onChange={(e, value) => {
                  updateUpcomingListItemAttributes(section._id, {
                    selectedTutorOption: value,
                    isDeleteChecked: false,
                  });
                }}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    size="small"
                    label="Tutor *"
                    placeholder="Select Tutor"
                    InputLabelProps={{
                      ...defaultTextFieldProps.InputLabelProps,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      ...defaultTextFieldProps.InputProps,
                      sx: {
                        ...((defaultTextFieldProps.InputProps || {}).sx || {}),
                        backgroundColor: '#FFFFFF',
                      },
                    }}
                  />
                )}
              />
            </div>
            <div>
              <FormControlLabel
                label="Delete"
                control={
                  <Checkbox
                    disabled={
                      Boolean(section.successMessage) || section.isLoading
                    }
                    checked={section.isDeleteChecked}
                    onChange={(event, value) =>
                      updateUpcomingListItemAttributes(section._id, {
                        isDeleteChecked: value,
                        selectedTutorOption: null,
                      })
                    }
                  />
                }
              />
            </div>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UpcomingClassItem;
